import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadOutlined,
  EditOutlined,
  FileExcelOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Modal,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd';
import {
  downloadJobPositionTemplate,
  importJobPositions,
} from '@/services/jobPositions.service';
import JobDescriptionList from './JobDescriptionList';
import JobPositionImportRowModal from './JobPositionImportRowModal';
import { jobPositionLevelShort, useJobPositionsT } from './useJobPositions';

const errorOf = (error) =>
  error?.response?.data?.message || error?.message || '';

const TONES = {
  zinc: 'text-zinc-700',
  emerald: 'text-emerald-700',
  amber: 'text-amber-700',
  red: 'text-red-700',
};

const Count = ({ label, value, tone = 'zinc' }) => (
  <div className="rounded-xl border border-zinc-200 bg-white px-3 py-2">
    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-zinc-400">
      {label}
    </div>
    <div className={`text-xl font-extrabold ${TONES[tone]}`}>{value}</div>
  </div>
);

/**
 * "Import from Excel": pick a file → preview of what would happen (new,
 * merged duplicates, completed, invalid rows) → confirm. The same file is
 * sent twice; the first time nothing is written.
 */
export default function JobPositionImportModal({
  companyId,
  open,
  onClose,
  onImported,
}) {
  const { tj } = useJobPositionsT();
  const { i18n } = useTranslation();
  const [file, setFile] = useState(null);
  const [plan, setPlan] = useState(null);
  // The file's rows as the user corrects them; `dirty` until checked again.
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [source, setSource] = useState(null);
  const [editing, setEditing] = useState(null);

  const reset = () => {
    setFile(null);
    setPlan(null);
    setRows([]);
    setDirty(false);
    setSource(null);
  };
  // The preview is cleared once the dialog has closed: cleared while it
  // still animates out, the dialog narrowed under the wide table.
  const close = () => onClose();

  const template = useMutation({
    mutationFn: () => downloadJobPositionTemplate(i18n.language),
    onError: (error) => message.error(errorOf(error) || tj('importFailed')),
  });

  const preview = useMutation({
    mutationFn: (picked) => importJobPositions(companyId, picked, false),
    onSuccess: (result) => {
      setPlan(result);
      setRows((result.rows || []).map((r) => ({ ...r, excluded: false })));
      setSource({ sheet: result.sheet, rows: result.summary.rows });
      setDirty(false);
    },
    onError: (error) => {
      message.error(errorOf(error) || tj('importFailed'));
      reset();
    },
  });

  const recheck = useMutation({
    mutationFn: () => importJobPositions(companyId, null, false, rows),
    onSuccess: (result) => {
      setPlan(result);
      setRows((current) => current.map((r) => ({ ...r, edited: false })));
      setDirty(false);
    },
    onError: (error) => message.error(errorOf(error) || tj('importFailed')),
  });

  const editRow = (row, patch) => {
    setRows((current) =>
      current.map((r) => (r.row === row ? { ...r, ...patch, edited: true } : r))
    );
    setDirty(true);
  };

  const apply = useMutation({
    mutationFn: () => importJobPositions(companyId, null, true, rows),
    onSuccess: (result) => {
      message.success(
        tj('importDone', {
          created: result.summary.create,
          updated: result.summary.update,
        })
      );
      onImported?.();
      close();
    },
    onError: (error) => message.error(errorOf(error) || tj('importFailed')),
  });

  const s = plan?.summary;

  // What happens to each row of the file, from the last check.
  const rowStatus = new Map();
  (plan?.invalid || []).forEach((i) =>
    rowStatus.set(i.row, { color: 'red', label: tj(`invalid.${i.reason}`) })
  );
  (plan?.items || []).forEach((i) => {
    rowStatus.set(i.row, {
      color: { create: 'green', update: 'blue', unchanged: 'default' }[
        i.action
      ],
      label: tj(`importStatus.${i.action}`),
    });
    (i.rows || [])
      .filter((r) => r !== i.row)
      .forEach((r) =>
        rowStatus.set(r, {
          color: 'orange',
          label: tj('importStatus.duplicate', { row: i.row }),
        })
      );
  });

  const rowColumns = [
    {
      title: tj('importStatus.include'),
      dataIndex: 'excluded',
      width: 70,
      align: 'center',
      render: (excluded, r) => (
        <Checkbox
          checked={!excluded}
          onChange={(e) => editRow(r.row, { excluded: !e.target.checked })}
        />
      ),
    },
    { title: '#', dataIndex: 'row', width: 50 },
    {
      title: tj('field.nameSr'),
      dataIndex: 'nameSr',
      ellipsis: true,
      render: (value, r) => (
        <div className={r.excluded ? 'opacity-40' : ''}>
          <div className="truncate font-semibold">{value || '—'}</div>
          {r.nameEn && (
            <div className="truncate text-xs text-zinc-500">{r.nameEn}</div>
          )}
        </div>
      ),
    },
    {
      title: tj('field.sectorName'),
      dataIndex: 'sectorName',
      width: 200,
      ellipsis: true,
    },
    {
      title: tj('field.educationLevel'),
      dataIndex: 'educationLevelRaw',
      width: 110,
    },
    {
      title: tj('field.description'),
      dataIndex: 'description',
      width: 260,
      ellipsis: { showTitle: false },
      render: (value) =>
        value ? (
          <Tooltip
            placement="topLeft"
            overlayStyle={{ maxWidth: 480 }}
            title={<JobDescriptionList text={value} className="max-h-72 overflow-auto" />}
          >
            {/* Anchored to the visible, truncated text: the full text is
                far wider than the cell and pulled the tooltip off to the
                side. */}
            <span className="block truncate text-zinc-600">{value}</span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: tj('importStatus.title'),
      key: 'status',
      width: 160,
      render: (_, r) => {
        if (r.excluded) return <Tag>{tj('importStatus.excluded')}</Tag>;
        if (dirty && r.edited) return <Tag>{tj('importStatus.unchecked')}</Tag>;
        const st = rowStatus.get(r.row);
        return st ? <Tag color={st.color}>{st.label}</Tag> : null;
      },
    },
    {
      key: 'edit',
      width: 50,
      align: 'center',
      render: (_, r) => (
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          aria-label={tj('importStatus.edit')}
          onClick={() => setEditing(r)}
        />
      ),
    },
  ];
  const changes = (plan?.items || []).filter((i) => i.action === 'update');
  const differences = (plan?.items || []).filter((i) => i.differences?.length);
  const rowsOf = (list) => list.join(', ');

  const sections = plan
    ? [
        plan.duplicates.length > 0 && {
          key: 'dup',
          label: tj('importDuplicates', { count: plan.duplicates.length }),
          children: (
            <ul className="m-0 grid gap-1.5 pl-4 text-[13px]">
              {plan.duplicates.map((d) => (
                <li key={d.nameSr}>
                  <b>{d.nameSr}</b> —{' '}
                  {tj('importRows', { rows: rowsOf(d.rows) })};{' '}
                  {tj('importKeptRow', { row: d.keptRow })}
                  {d.conflicts.length > 0 && (
                    <div className="text-amber-700">
                      {d.conflicts.map((c) => (
                        <div key={`${c.field}-${c.row}`}>
                          {tj(`field.${c.field}`)}: „{String(c.kept ?? '')}“ ≠ „
                          {String(c.other ?? '')}“ (
                          {tj('importRow', { row: c.row })})
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ),
        },
        plan.invalid.length > 0 && {
          key: 'invalid',
          label: tj('importInvalid', { count: plan.invalid.length }),
          children: (
            <ul className="m-0 pl-4 text-[13px]">
              {plan.invalid.map((i) => (
                <li key={i.row}>
                  {tj('importRow', { row: i.row })}
                  {i.nameSr ? ` — ${i.nameSr}` : ''}:{' '}
                  {tj(`invalid.${i.reason}`)}
                </li>
              ))}
            </ul>
          ),
        },
        changes.length > 0 && {
          key: 'update',
          label: tj('importUpdates', { count: changes.length }),
          children: (
            <ul className="m-0 pl-4 text-[13px]">
              {changes.map((i) => (
                <li key={i.row}>
                  <b>{i.nameSr}</b>:{' '}
                  {i.changes.map((f) => tj(`field.${f}`)).join(', ')}
                </li>
              ))}
            </ul>
          ),
        },
        differences.length > 0 && {
          key: 'diff',
          label: tj('importDifferences', { count: differences.length }),
          children: (
            <ul className="m-0 pl-4 text-[13px]">
              {differences.map((i) => (
                <li key={i.row}>
                  <b>{i.nameSr}</b>:{' '}
                  {i.differences
                    .map(
                      (d) =>
                        `${tj(`field.${d.field}`)} „${d.stored}“ → „${d.file}“`
                    )
                    .join('; ')}
                </li>
              ))}
            </ul>
          ),
        },
        s?.levelsKeptAsText > 0 && {
          key: 'levels',
          label: tj('importLevelsAsText', { count: s.levelsKeptAsText }),
          children: (
            <div className="flex flex-wrap gap-1">
              {[
                ...new Set(
                  plan.items
                    .filter((i) => i.educationLevel === 'other')
                    .map(jobPositionLevelShort)
                ),
              ].map((text) => (
                <Tag key={text}>{text}</Tag>
              ))}
            </div>
          ),
        },
      ].filter(Boolean)
    : [];

  return (
    <Modal
      open={open}
      onCancel={close}
      afterClose={reset}
      width={plan ? 1100 : 760}
      title={
        <span className="flex items-center gap-2">
          <FileExcelOutlined /> {tj('importTitle')}
        </span>
      }
      footer={
        plan
          ? [
              <Button key="back" className="!rounded-[50px]" onClick={reset}>
                {tj('importOtherFile')}
              </Button>,
              dirty ? (
                <Button
                  key="recheck"
                  type="primary"
                  className="!rounded-[50px]"
                  loading={recheck.isPending}
                  onClick={() => recheck.mutate()}
                >
                  {tj('importRecheck')}
                </Button>
              ) : (
                <Button
                  key="ok"
                  type="primary"
                  className="!rounded-[50px]"
                  disabled={!s || s.create + s.update === 0}
                  loading={apply.isPending}
                  onClick={() => apply.mutate()}
                >
                  {tj('importConfirm', { count: s ? s.create + s.update : 0 })}
                </Button>
              ),
            ]
          : null
      }
    >
      {!plan && (
        <Spin spinning={preview.isPending}>
          <p className="mb-2 text-[13px] text-zinc-500">{tj('importHint')}</p>
          <Button
            className="mb-3 !rounded-[50px]"
            icon={<DownloadOutlined />}
            loading={template.isPending}
            onClick={() => template.mutate()}
          >
            {tj('importTemplate')}
          </Button>
          <Upload.Dragger
            accept=".xlsx"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(picked) => {
              setFile(picked);
              preview.mutate(picked);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{tj('importDrop')}</p>
          </Upload.Dragger>
        </Spin>
      )}

      {plan && (
        <div className="grid gap-3">
          <div className="text-[13px] text-zinc-500">
            {file?.name} ·{' '}
            {tj('importSheet', { sheet: source?.sheet, rows: source?.rows })}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Count label={tj('importNew')} value={s.create} tone="emerald" />
            <Count
              label={tj('importMerged')}
              value={s.duplicateRowsMerged}
              tone="amber"
            />
            <Count label={tj('importCompleted')} value={s.update} />
            <Count
              label={tj('importInvalidCount')}
              value={s.invalid}
              tone="red"
            />
          </div>
          {s.unchanged > 0 && (
            <Alert
              type="info"
              showIcon
              message={tj('importUnchanged', { count: s.unchanged })}
            />
          )}
          {sections.length > 0 && <Collapse size="small" items={sections} />}
          <div className="text-[13px] font-semibold text-zinc-700">
            {tj('importRowsTitle')}
          </div>
          {dirty && (
            <Alert type="warning" showIcon message={tj('importEditedHint')} />
          )}
          <Table
            size="small"
            rowKey="row"
            dataSource={rows}
            columns={rowColumns}
            loading={recheck.isPending}
            scroll={{ y: 380 }}
            tableLayout="fixed"
            pagination={{ pageSize: 50, hideOnSinglePage: true }}
          />
          <JobPositionImportRowModal
            row={editing}
            onClose={() => setEditing(null)}
            onSave={(patch) => {
              editRow(editing.row, patch);
              setEditing(null);
            }}
          />
        </div>
      )}
    </Modal>
  );
}
